import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import Logo from "@/components/Logo";
import { bookCtaClick } from "@/lib/bookCta";
import { PHONE_HREF, PHONE_DISPLAY, trackPhoneCall } from "@/lib/contact";

const navItems = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/blog", label: "Blog" },
  { to: "/contact", label: "Contact Us" },
  { to: "/about", label: "About Us" },
  { to: "/privacy", label: "Privacy Policy" },
  { to: "/terms", label: "Terms And Conditions" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const handleBook = (e) => bookCtaClick(e, navigate);

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 bg-black/70 backdrop-blur-xl border-b border-white/10"
    >
      <div className="mx-auto max-w-7xl px-4 md:px-8 lg:px-12">
        <div className="flex items-center justify-between h-36">
          <Logo />

          <nav className="hidden lg:flex items-center gap-1" data-testid="primary-nav">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}-link`}
                className={({ isActive }) =>
                  `relative px-3 py-2 text-sm font-medium transition-colors ${
                    isActive ? "text-[#FE7200]" : "text-zinc-300 hover:text-white"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <a
              href={PHONE_HREF}
              onClick={trackPhoneCall}
              data-testid="header-phone-link"
              className="hidden xl:inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2 text-sm font-semibold text-white hover:text-[#FE7200] hover:border-[#FE7200]/50 transition-colors"
            >
              <Phone className="h-3.5 w-3.5 text-[#FE7200]" />
              {PHONE_DISPLAY}
            </a>
            <a
              href="#book-form"
              onClick={handleBook}
              data-testid="header-cta-button"
              className="btn-shine pulse-glow inline-flex items-center justify-center rounded-full bg-[#FE7200] px-5 py-2.5 text-sm font-bold text-black hover:bg-[#FF8C2E] transition-all duration-300 hover:scale-105"
            >
              Book Free Site Visit
            </a>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden inline-flex items-center justify-center h-10 w-10 rounded-md border border-white/10 text-white"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {open && (
          <div
            className="lg:hidden pb-6 pt-2 border-t border-white/10"
            data-testid="mobile-nav"
          >
            <div className="flex flex-col gap-1">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}-link`}
                  className={({ isActive }) =>
                    `px-3 py-3 rounded-md text-base ${
                      isActive
                        ? "text-[#FE7200] bg-white/5"
                        : "text-zinc-200 hover:bg-white/5"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
              <a
                href="#book-form"
                onClick={(e) => {
                  setOpen(false);
                  handleBook(e);
                }}
                data-testid="mobile-header-cta-button"
                className="mt-3 inline-flex items-center justify-center rounded-full bg-[#FE7200] px-5 py-3 text-sm font-bold text-black"
              >
                Book Free Site Visit
              </a>
              <a
                href={PHONE_HREF}
                onClick={() => { trackPhoneCall(); setOpen(false); }}
                data-testid="mobile-header-phone-link"
                className="mt-2 inline-flex items-center justify-center gap-2 rounded-full border border-[#FE7200]/40 bg-[#FE7200]/10 px-5 py-3 text-sm font-bold text-[#FE7200]"
              >
                <Phone className="h-4 w-4" /> Call {PHONE_DISPLAY}
              </a>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
